import React, { useEffect, useMemo, useRef, useState } from 'react';
import { notePreviewEmptyText } from '../../../domain/lessonAttendance';
import useNoteLibrary from '../../../hooks/useNoteLibrary';
import m from './notePreview.module.css';

// The rendered half of the lesson-note editor: what the teacher's Markdown
// becomes once something draws it, so a typo in a table is found here rather
// than in a family's inbox. The subset is docs/features/lesson-note-markdown.md.
//
// ── What this preview matches, and what it does not ─────────────────────────
//
// It matches the structure and emphasis: one renderer draws every surface
// (src/domain/markdown.js argues the one-renderer property), so the tags, the
// nesting and the text here are byte-identical to the email's. If a table
// renders here it renders there; if a link degrades to plain text here (an
// `ftp://` one, say) it degrades there too.
//
// It does not match the type scale and the colours: this is the app's 14px
// Inter on the app's surfaces, and an inbox has its own font, width and dark
// mode. Every element the subset can produce is styled here, so nothing
// renders naked in the drawer and dressed in the email, but the two are not
// the same picture.
//
// ── The skin is `classes`, not `styles` ─────────────────────────────────────
//
// The two-skin seam in markdown.js lets this file and
// functions/delivery/templates.js dress one parse differently. Email has no
// stylesheet and takes `styles`; the web takes `classes`, so the styling lives
// in a CSS module where the theme check can see it. CLASSES below is the whole
// vocabulary this renderer can emit, minus the five tags noted there.
//
// ── Why the import is dynamic ───────────────────────────────────────────────
//
// markdown-it is about 49 KB min+gzip, dead weight on every screen that never
// draws a note, so it is fetched when the preview opens and lands in its own
// chunk. Nothing else in src/ imports src/domain/markdown.js statically; a
// static import anywhere would pull it back into the entry chunk and nothing
// would report it.
//
// The resolved function is kept at module scope rather than in state, so every
// open after the first renders on the first paint instead of flashing the
// loading line for a render pass.
let renderMarkdown = null;

// Tag → CSS-module class. Keys must be tags the renderer emits: markdown.js
// throws on anything else rather than ignoring it, and that throw is caught
// below (`failed`).
//
// Five of the 25 skinnable tags are absent: `em`, `strong` and `s` are drawn
// correctly by the browser at this size, and `tbody` and `tr` have nothing to
// say once the borders are on the cells. If SKINNABLE_TAGS grows, this map
// must grow with it: an unkeyed tag is silently unstyled.
const CLASSES = {
  a: m.a,
  blockquote: m.blockquote,
  code: m.code,
  h1: m.h1,
  h2: m.h2,
  h3: m.h3,
  h4: m.h4,
  h5: m.h5,
  h6: m.h6,
  hr: m.hr,
  img: m.img,
  li: m.li,
  ol: m.ol,
  p: m.p,
  pre: m.pre,
  table: m.table,
  td: m.td,
  th: m.th,
  thead: m.thead,
  ul: m.ul,
};

// A click on a rendered link, intercepted because this panel sits in a drawer
// over an unsaved form: following an http(s) link in place would unmount the
// drawer and throw away the note being previewed.
//
// `mailto:` and `tel:` are left alone: they hand off to an OS handler and the
// page stays put, and window.open would leave a blank tab in several browsers.
function openLinksAway(event) {
  const anchor = event.target?.closest?.('a[href]');
  if (!anchor || !/^https?:/i.test(anchor.href)) return;
  // Only swallow the navigation if the new tab opened, so a popup blocker
  // cannot make a link do nothing.
  if (window.open(anchor.href, '_blank', 'noopener,noreferrer')) event.preventDefault();
}

// A library picture whose bytes do not arrive — offline, or a link that
// expired under a tab left open for a month — becomes its alt text, the
// file's name unless the teacher wrote another, as the renderer already draws
// a picture it could not resolve: never a broken-image box. On the panel in
// the capture phase, because an image's `error` does not bubble.
function namePicturesThatFail(event) {
  const img = event.target;
  if (!img || img.tagName !== 'IMG') return;
  img.replaceWith(document.createTextNode(img.getAttribute('alt') || ''));
}

/**
 * The rendered form of one lesson note.
 *
 * @param {string} source  the note as stored — Markdown source, as text
 * @param {string} label   which of the three bodies this is: Student, Parent, Private
 * @param {string} [id]    so the toggle that opened it can name it in aria-controls
 *
 * `caption` and `emptyText` are New Message's (#596), whose message is not one
 * of the three notes: the panel's second caption and the line for an empty
 * message. Without them it speaks of `label`'s note.
 */
export default function NotePreview({
  source, label, id, caption = null, emptyText = null,
}) {
  const [render, setRender] = useState(() => renderMarkdown);
  const [unavailable, setUnavailable] = useState(false);
  // The library pictures this note names, each with a link of ours (#531);
  // null until they are resolved, and with no library provider at all.
  const library = useNoteLibrary(source);
  const bodyRef = useRef(null);

  // Keyed on `render` rather than run once: a previous instance's import can
  // resolve between this one's first render and its first effect, and a bare
  // `if (renderMarkdown) return` would leave the panel on "Rendering…". Asking
  // for the module again is free; the browser has the chunk.
  useEffect(() => {
    if (render) return undefined;
    let live = true;
    // Two arguments rather than `.then(…).catch(…)` (#184), so a bug in the
    // success handler is not reported as a failed chunk fetch.
    import('../../../domain/markdown.js').then(
      (mod) => {
        renderMarkdown = mod.renderMarkdown;
        if (live) setRender(() => mod.renderMarkdown);
      },
      // A chunk fetch that fails — a flaky connection, or a deploy that rotated
      // the asset while this tab stayed open. Said plainly, and so that it is
      // clear the note itself is untouched: the stored value is the text in the
      // box above.
      () => { if (live) setUnavailable(true); },
    );
    return () => { live = false; };
  }, [render]);

  // Re-parsed as the teacher types. Memoised so a re-render for any other
  // reason — switching audience, a parent re-render — does not re-run it.
  const { html, failed } = useMemo(() => {
    if (!render) return { html: null, failed: false };
    try {
      return { html: render(source, { classes: CLASSES, library }), failed: false };
    } catch (err) {
      // CLASSES is a constant, so this is a bug in the map above, not something
      // a note can cause. Loud in the console; quiet in the drawer, so the
      // teacher loses the panel rather than the screen.
      console.error('Lesson-note preview could not render:', err);
      return { html: null, failed: true };
    }
  }, [render, source, library]);

  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return undefined;
    node.addEventListener('error', namePicturesThatFail, true);
    return () => node.removeEventListener('error', namePicturesThatFail, true);
  }, [html]);

  let panel;
  if (unavailable || failed) {
    panel = (
      <div className={m.quiet}>
        The preview could not be loaded. Your note is saved exactly as you typed it.
      </div>
    );
  } else if (!render) {
    panel = <div className={m.quiet}>Rendering…</div>;
  } else if (!html) {
    // renderMarkdown returns '' for an empty or whitespace-only note. An empty
    // bordered box would read as the note having been lost, so the panel says
    // which body is empty.
    panel = <div className={m.quiet}>{emptyText || notePreviewEmptyText(label)}</div>;
  } else {
    // Not the risk the name implies (src/domain/markdown.js): there is no input
    // markup. `html` is false, so the parser never produces an html_block or
    // html_inline token; the teacher's characters are escaped; and every tag in
    // this string comes from a fixed vocabulary. A note containing <script> is
    // escaped, and the teacher reads back the eight characters they typed.
    panel = <div ref={bodyRef} className={m.body} onClick={openLinksAway} dangerouslySetInnerHTML={{ __html: html }} />;
  }

  return (
    <div className={m.panel} id={id}>
      {/* Which body this is, restated inside the panel: the audience pills are
          at the top of a drawer that scrolls and can be off screen by the time
          a long note is rendered. */}
      <div className={m.caption}>
        <span>Preview</span>
        <span>{caption || `${label} note`}</span>
      </div>
      {panel}
    </div>
  );
}
